import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import MarketingLayout from './components/MarketingLayout'
import { API_URL } from '../../store'

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
}
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
}

const initialForm = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  agreeTerms: false,
  agreePrivacy: false,
}

function ContactPage() {
  const [form, setForm] = useState(initialForm)
  const [errors, setErrors] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  useEffect(() => {
    document.title = 'Contact Us | SOS SMS'
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      'Get in touch with SOS SMS — email support, business hours, or send us a message about customer support, billing, or becoming a provider.'
    )
  }, [])

  const validate = () => {
    const e = {}
    if (!form.name.trim()) e.name = 'Name is required.'
    if (!form.email.trim()) {
      e.email = 'Email is required.'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = 'Please enter a valid email address.'
    }
    if (!form.subject.trim()) e.subject = 'Please select a subject.'
    if (!form.message.trim() || form.message.trim().length < 10) {
      e.message = 'Message must be at least 10 characters.'
    }
    if (!form.agreeTerms) e.agreeTerms = 'You must agree to the Terms & Conditions.'
    if (!form.agreePrivacy) e.agreePrivacy = 'You must accept the Privacy Policy.'
    return e
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }))
    if (errors[name]) setErrors((err) => { const n = { ...err }; delete n[name]; return n })
    if (submitError) setSubmitError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const e2 = validate()
    if (Object.keys(e2).length) { setErrors(e2); return }
    setSubmitting(true)

    try {
      const response = await fetch(`${API_URL}api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          subject: form.subject.trim(),
          message: form.message.trim(),
        }),
      })

      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.message || 'Unable to send message right now.')
      }

      setSubmitting(false)
      setSubmitted(true)
      setSubmitError('')
      setForm(initialForm)
    } catch (error) {
      setSubmitting(false)
      setSubmitError(error.message)
    }
  }

  return (
    <MarketingLayout>

      {/* ── HERO ──────────────────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-green-900 via-green-800 to-emerald-700 text-white py-24 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid4" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid4)" />
          </svg>
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.p variants={fadeUp} className="text-green-300 font-semibold text-sm uppercase tracking-wider mb-4">
              Contact Us
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl font-extrabold leading-tight mb-5">
              We're Here to Help
            </motion.h1>
            <motion.p variants={fadeUp} className="text-green-100 text-lg leading-relaxed max-w-xl mx-auto">
              Have questions about SOS SMS? Want to learn more about becoming a provider? Our team is ready to assist.
            </motion.p>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" className="w-full h-12 fill-white" preserveAspectRatio="none">
            <path d="M0,32 C360,60 1080,0 1440,32 L1440,60 L0,60 Z" />
          </svg>
        </div>
      </section>

      {/* ── CONTACT SECTION ───────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-14">

            {/* Left: Info */}
            <motion.div
              className="lg:col-span-2"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.h2 variants={fadeUp} className="text-2xl font-extrabold text-gray-900 mb-6">
                Get in Touch
              </motion.h2>

              {[
                {
                  icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  ),
                  label: 'Email',
                  value: 'support@sossms.com',
                  href: 'mailto:support@sossms.com',
                },
                {
                  icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M3 6h18M5 12h14M5 18h14" />
                    </svg>
                  ),
                  label: 'Mailing Address',
                  value: '7707 Stickney\nWauwatosa, WI 53213',
                  href: null,
                },
                {
                  icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ),
                  label: 'Business Hours',
                  value: 'Mon – Fri: 9AM – 6PM EST',
                  href: null,
                },
              ].map((item) => (
                <motion.div key={item.label} variants={fadeUp} className="flex items-start gap-4 mb-6">
                  <div className="w-11 h-11 rounded-xl bg-green-50 text-primary flex items-center justify-center flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm mb-0.5">{item.label}</div>
                    {item.href ? (
                      <a href={item.href} className="text-primary text-sm hover:underline">{item.value}</a>
                    ) : (
                      <p className="text-gray-500 text-sm whitespace-pre-line">{item.value}</p>
                    )}
                  </div>
                </motion.div>
              ))}

              {/* Quick links */}
              <motion.div variants={fadeUp} className="mt-8 p-6 bg-gray-50 rounded-2xl border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-4">Quick Links</h3>
                <div className="space-y-2.5">
                  <Link to="/customer/signup" className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors">
                    <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                    Sign up as a Customer
                  </Link>
                  <Link to="/provider/signup" className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors">
                    <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                    Join as a Service Provider
                  </Link>
                  <Link to="/services" className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors">
                    <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                    View our Services
                  </Link>
                  <Link to="/login" className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors">
                    <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                    Log in to your Account
                  </Link>
                </div>
              </motion.div>
            </motion.div>

            {/* Right: Form */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0, transition: { duration: 0.6 } }}
              viewport={{ once: true }}
            >
              {submitted ? (
                <div className="flex flex-col items-center justify-center text-center py-20 bg-green-50 rounded-3xl border border-green-100">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-5">
                    <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-extrabold text-gray-900 mb-3">Message Sent!</h3>
                  <p className="text-gray-500 max-w-sm">
                    Thank you for reaching out. Our team will get back to you within 1 business day.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-8 px-6 py-3 bg-primary text-white font-semibold rounded-xl hover:bg-green-700 transition-colors"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  noValidate
                  className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 lg:p-10"
                >
                  <h2 className="text-xl font-extrabold text-gray-900 mb-7">Send Us a Message</h2>

                  {submitError && (
                    <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {submitError}
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-5 mb-5">
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        className={`w-full px-4 py-3 border rounded-xl text-sm outline-none transition-colors ${errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-primary bg-gray-50 focus:bg-white'}`}
                      />
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className={`w-full px-4 py-3 border rounded-xl text-sm outline-none transition-colors ${errors.email ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-primary bg-gray-50 focus:bg-white'}`}
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5 mb-5">
                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone (optional)</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+1 (555) 000-0000"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none bg-gray-50 focus:border-primary focus:bg-white transition-colors"
                      />
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Subject <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-xl text-sm outline-none transition-colors ${errors.subject ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:border-primary focus:bg-white'}`}
                      >
                        <option value="">Select a subject...</option>
                        <option value="general">General Inquiry</option>
                        <option value="customer-support">Customer Support</option>
                        <option value="provider-inquiry">Provider / Business Inquiry</option>
                        <option value="billing">Billing Question</option>
                        <option value="technical">Technical Issue</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.subject && <p className="text-red-500 text-xs mt-1">{errors.subject}</p>}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Tell us how we can help you..."
                      className={`w-full px-4 py-3 border rounded-xl text-sm outline-none resize-none transition-colors ${errors.message ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:border-primary focus:bg-white'}`}
                    />
                    {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                  </div>

                  {/* Compliance checkboxes */}
                  <div className="space-y-3 mb-7">
                    <label className={`flex items-start gap-3 cursor-pointer group ${errors.agreeTerms ? 'text-red-600' : 'text-gray-600'}`}>
                      <input
                        type="checkbox"
                        name="agreeTerms"
                        checked={form.agreeTerms}
                        onChange={handleChange}
                        className="mt-0.5 w-4 h-4 accent-primary flex-shrink-0"
                      />
                      <span className="text-sm">
                        I agree to the{' '}
                        <Link to="/terms" className="text-primary underline hover:text-green-700" target="_blank" rel="noopener noreferrer">
                          Terms & Conditions
                        </Link>
                      </span>
                    </label>
                    {errors.agreeTerms && <p className="text-red-500 text-xs -mt-1 ml-7">{errors.agreeTerms}</p>}

                    <label className={`flex items-start gap-3 cursor-pointer ${errors.agreePrivacy ? 'text-red-600' : 'text-gray-600'}`}>
                      <input
                        type="checkbox"
                        name="agreePrivacy"
                        checked={form.agreePrivacy}
                        onChange={handleChange}
                        className="mt-0.5 w-4 h-4 accent-primary flex-shrink-0"
                      />
                      <span className="text-sm">
                        I have read and accept the{' '}
                        <Link to="/privacy-policy" className="text-primary underline hover:text-green-700" target="_blank" rel="noopener noreferrer">
                          Privacy Policy
                        </Link>
                      </span>
                    </label>
                    {errors.agreePrivacy && <p className="text-red-500 text-xs -mt-1 ml-7">{errors.agreePrivacy}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3.5 bg-primary text-white font-bold rounded-xl hover:bg-green-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>

          </div>
        </div>
      </section>

    </MarketingLayout>
  )
}

export default ContactPage
